function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
function withViewTransition(update) {
  if (document.startViewTransition && document.body.classList.contains('animations-true') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.startViewTransition(update);
  } else {
    update();
  }
}
/**
 * Publish a Shopify standard event (shopify:* DOM events for analytics apps).
 * The event classes are loaded async from Shopify's CDN via a module script in
 * theme.liquid — no-op until the module is available, and never let an
 * analytics payload error break a commerce flow.
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 */
var publishStandardEvent = function publishStandardEvent(eventName, payload, target) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !SE[eventName]) return;
  try {
    (target || document).dispatchEvent(new SE[eventName](payload || {}));
  } catch (e) {
    console.error('Standard event failed:', eventName, e);
  }
};
/**
 * Ajax API error text -> shopify:cart:error payload. The Ajax API returns no
 * error codes, so every failure maps to the generic INVALID code.
 */
var standardEventCartError = function standardEventCartError(message) {
  return { error: String(message || 'Cart update failed'), code: 'INVALID' };
};
/**
 * Cents -> decimal amount string ("29.90"), the MoneyV2 shape standard events expect.
 */
var standardEventAmount = function standardEventAmount(cents) {
  const value = parseInt(cents, 10);
  return (Number.isNaN(value) ? 0 : value / 100).toFixed(2);
};

/**
 * Standard-event adapters: translate the theme's internal custom events into
 * Shopify standard events for pixel/analytics apps.
 */
(function () {
  const SE = () => window.Shopify && window.Shopify.StandardEvents;
  const ajaxCart = (cart) => SE().CartLinesUpdateEvent.createCartFromAjaxResponse(cart);

  // Cart drawer opened -> cart view. The drawer markup carries no cart JSON,
  // so resolve the cart with a small user-triggered /cart.js fetch.
  document.addEventListener('cart-drawer:open', () => {
    if (!SE()) return;
    fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || !cart.token) return;
        publishStandardEvent('CartViewEvent', { cart: ajaxCart(cart), context: 'dialog' });
      })
      .catch(() => { /* analytics only — never surface */ });
  });

  // Item added via /cart/add.js -> lines update. The add response is only the
  // added line, not a full cart, so the promise resolves with a null cart. The
  // detail carries the submitted quantity (the response quantity is the line's
  // new total) and where the add happened (product page, quick view dialog).
  document.addEventListener('cart:item-added', (e) => {
    const detail = e.detail || {};
    const item = detail.product;
    const variantId = item && (item.variant_id || item.id);
    if (!variantId) return;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: 'add',
      context: detail.context || 'product',
      lines: [{ merchandiseId: variantId, quantity: parseInt(detail.quantity, 10) || 1 }],
      promise: Promise.resolve({ cart: null })
    });
  });

  // Quantity changed or line removed -> lines update. The change response in
  // the event detail is already a full cart — reuse it instead of refetching.
  // Early SDK guard: the payload below calls into the SDK while it is built,
  // which happens before the publish helper can no-op.
  document.addEventListener('line-item:change:end', (e) => {
    if (!SE()) return;
    const detail = e.detail || {};
    const cart = detail.cart;
    // A /cart/change.js failure comes back as { status, message, description }
    // (or { errors }) instead of a cart -> cart error.
    if (cart && (cart.errors || cart.status)) {
      publishStandardEvent('CartErrorEvent', standardEventCartError(cart.errors || cart.description || cart.message));
      return;
    }
    // The schema needs a line id; without the key there is no valid payload.
    if (!detail.key) return;
    const quantity = parseInt(detail.quantity, 10) || 0;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: quantity === 0 ? 'remove' : 'update',
      context: detail.context || 'cart',
      lines: [{ id: detail.key, quantity: quantity }],
      promise: Promise.resolve({ cart: cart && cart.token && cart.items ? ajaxCart(cart) : null })
    });
  });

  // Add-to-cart failure -> cart error.
  document.addEventListener('product:variant-error', (e) => {
    const detail = e.detail || {};
    publishStandardEvent('CartErrorEvent', standardEventCartError(detail.errors || detail.message));
  });
})();

var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};

/**
 *  @object
 *  @function OverlayManager
 *  @description Stack-based overlay manager for side panels, drawers, and modals.
 */
const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),
  extraBodyClassMap: new Map(),
  lastFocused: new Map(),
  _trapHandler: null,
  focusableSelector: 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  cc: null,

  trapFocus(e) {
    if (e.key !== 'Tab' || !this.stack.length) return;
    const panel = this.stack[this.stack.length - 1];
    const focusable = Array.from(panel.querySelectorAll(this.focusableSelector)).filter((el) => el.getClientRects().length);
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const inside = panel.contains(document.activeElement);
    if (e.shiftKey) {
      if (!inside || document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else if (!inside || document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  },

  getCC() {
    if (!this.cc) {
      this.cc = document.querySelector('.click-capture');
    }
    return this.cc;
  },

  updateCCZIndex() {
    const cc = this.getCC();
    if (!cc) return;
    if (this.stack.length > 1) {
      const topPanel = this.stack[this.stack.length - 1];
      const topZ = parseInt(getComputedStyle(topPanel).zIndex, 10) || 90;
      cc.style.zIndex = topZ - 1;
    } else {
      cc.style.zIndex = '';
    }
  },

  open(panel, extraBodyClasses) {
    if (!panel) return;
    // If already in stack, move to top; only remember the opener on first open
    const index = this.stack.indexOf(panel);
    if (index === -1) {
      this.lastFocused.set(panel, document.activeElement);
    } else {
      this.stack.splice(index, 1);
    }
    if (!this.stack.length) {
      if (!this._trapHandler) this._trapHandler = this.trapFocus.bind(this);
      document.addEventListener('keydown', this._trapHandler);
    }
    this.stack.push(panel);
    if (extraBodyClasses && extraBodyClasses.length) {
      this.extraBodyClassMap.set(panel, extraBodyClasses);
      document.body.classList.add(...extraBodyClasses);
    }
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    this.updateCCZIndex();
    const closeBtn = panel.querySelector('.side-panel-close');
    if (closeBtn) closeBtn.focus();
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    const extraClasses = this.extraBodyClassMap.get(panel);
    if (extraClasses) {
      document.body.classList.remove(...extraClasses);
      this.extraBodyClassMap.delete(panel);
    }
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
      if (this._trapHandler) {
        document.removeEventListener('keydown', this._trapHandler);
      }
    }
    this.updateCCZIndex();
    panel.classList.remove('active');
    panel.inert = true;

    const lastFocused = this.lastFocused.get(panel);
    if (!this.stack.length && lastFocused && lastFocused.isConnected) {
      lastFocused.focus();
    }
    this.lastFocused.delete(panel);

    dispatchCustomEvent('panel:closed', { panel });

    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) handler();
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  }
};

/**
 *  Deferred video loading — stamps <template class="background-video__deferred">
 *  into the live DOM when the parent nears the viewport.
 */
document.querySelectorAll('.background-video__deferred').forEach(function (template) {
  if (template.closest('background-video') || template.closest('slide-show') || template.closest('image-with-text')) return;
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        observer.disconnect();
        var clone = template.content.cloneNode(true);
        template.replaceWith(clone);
      }
    });
  }, { rootMargin: '100px' });
  observer.observe(template.parentElement || template);
});

window.recentlyViewedIds = [];

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));

    }
    connectedCallback() {
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }
      // Change quantity
      quantity += change;

      // Ensure quantity is always a number
      quantity = Math.max(quantity, 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.swatches = this.querySelector('.product-card-swatches');
      this.secondary = this.querySelector('.product-secondary-image');
      this.image = this.secondary || this.querySelector('.product-primary-image');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');
    }
    connectedCallback() {
      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
      if (this.secondary) {
        window.addEventListener('load', (event) => {
          lazySizes.loader.unveil(this.secondary);
        });
      }
      if (this.size_options) {
        this.enableSizeOptions();
      }

    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';
      this.color_index = this.swatches.dataset.index;

      swatch_list.forEach((swatch, index) => {
        window.addEventListener('load', (event) => {
          let image = new Image();
          image.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(image);
        });
        swatch.addEventListener('mouseover', () => {

          [].forEach.call(swatch_list, function (el) {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }
          if (this.size_options) {
            this.current_options[this.color_index] = swatch.querySelector('span').innerText;
            this.updateMasterId();
          }
          swatch.classList.add('active');
        });
        swatch.addEventListener('click', function (evt) {
          window.location.href = this.dataset.href;
          evt.preventDefault();
        });
      });
    }
    enableQuickAdd() {
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }
    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });
      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });
      size_list.forEach((size) => {
        size.addEventListener('click', (evt) => {
          evt.preventDefault();

          if (size.classList.contains('is-disabled')) {
            return;
          }
          this.current_options[this.size_index] = size.querySelector('span').innerText;
          this.updateMasterId();

          size.classList.add('loading');
          size.setAttribute('aria-disabled', true);
          const config = {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/javascript'
            }
          };
          let formData = new FormData();

          formData.append('id', this.currentVariant.id);
          formData.append('quantity', 1);
          formData.append('sections', this.getSectionsToRender().map((section) => section.section));
          formData.append('sections_url', window.location.pathname);

          config.body = formData;

          fetch(`${theme.routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                return;
              }
              this.renderContents(response);

              dispatchCustomEvent('cart:item-added', {
                product: response.hasOwnProperty('items') ? response.items[0] : response,
                quantity: 1,
                context: 'product'
              });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              size.classList.remove('loading');
              size.removeAttribute('aria-disabled');
            });
        });
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });
      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }
    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
    setDisabled() {
      const variant_data = this.getVariantData();

      if (variant_data) {

        if (this.currentVariant) {
          const selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });

          const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

          const fieldset_options = Object.values(available_options)[this.size_index];
          if (fieldset_options) {
            if (this.size_options.querySelectorAll('.product-card-sizes--size').length) {
              this.size_options.querySelectorAll('.product-card-sizes--size').forEach((input, input_i) => {
                input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
              });
            }
          }
        } else {
          if (this.size_options.querySelectorAll('.product-card-sizes--size').length) {
            this.size_options.querySelectorAll('.product-card-sizes--size').forEach((input, input_i) => {
              input.classList.add('is-disabled');
            });
          }
        }

      }
      return true;
    }
    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.getAttribute('disabled') != undefined) {
        return;
      }
      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      let formData = new FormData();

      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response,
            quantity: 1,
            context: 'product'
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.quick_add.classList.remove('loading');
          this.quick_add.removeAttribute('aria-disabled');
        });

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));


      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        cartDrawer.notesToggle();
        cartDrawer.discountToggle();
        cartDrawer.updateFreeShipping();
        OverlayManager.open(cartDrawer, ['open-cart']);
        const recs = cartDrawer.querySelector('.product-recommendations--full');
        if (recs) recs.classList.add('active');
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}



/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {

    constructor() {
      super();
      this.panel = this.closest('.side-panel');
    }

    connectedCallback() {
      const cc = document.querySelector('.click-capture');

      this.addEventListener('click', (e) => this.handleClose(e));
      this.addEventListener('keydown', (e) => this.handleClose(e));

      if (cc && !cc.dataset.overlayBound) {
        cc.dataset.overlayBound = '1';
        cc.addEventListener('click', () => OverlayManager.closeLast());
      }

      document.addEventListener('panel:close', () => OverlayManager.closeLast());
    }

    handleClose(e) {
      if (e.type === 'keydown' && e.code && e.code.toUpperCase() !== 'ENTER') return;
      if (this.panel) {
        OverlayManager.close(this.panel);
      } else {
        OverlayManager.closeLast();
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  document.addEventListener('keyup', (e) => {
    if (e.code && e.code.toUpperCase() === 'ESCAPE') {
      dispatchCustomEvent('panel:close');
    }
  });
}
/**
 *  @class
 *  @function CartDrawer
 */
class CartDrawer extends HTMLElement {

  constructor() {
    super();
  }

  connectedCallback() {
    this.button = document.getElementById('cart-drawer-toggle');

    // Add functionality to buttons
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      OverlayManager.open(this, ['open-cart']);
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      });
      dispatchCustomEvent('cart-drawer:open');
    });

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    document.addEventListener('cart:refresh', (event) => {
      this.refresh();
    });

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    this.notesToggle();
    this.discountToggle();
    this.updateFreeShipping();

    OverlayManager.registerBeforeClose(this, () => {
      let notes = this.querySelector('#mini-cart-note');
      let discount = this.querySelector('#mini-cart-discount');
      if (notes) notes.classList.remove('active');
      if (discount) discount.classList.remove('active');
    });
  }
  onChange(event) {
    if (event.target.classList.contains('qty')) {
      this.updateQuantity(event.target.dataset.key, event.target.value, event.target.dataset.index);
    }
  }
  getSectionsToRender() {
    return [{
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer-inner'
    },
    {
      id: 'cart-drawer-toggle',
      section: 'cart-bubble',
      selector: '.thb-item-count'
    }];
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
  termsCheckbox() {
    let terms_checkbox = this.querySelector('#CartDrawerTerms'),
      checkout_button = this.querySelector('.button.checkout');

    if (terms_checkbox && checkout_button) {
      terms_checkbox.setCustomValidity(theme.strings.requiresTerms);
      checkout_button.addEventListener('click', function (e) {
        if (!terms_checkbox.checked) {
          terms_checkbox.reportValidity();
          terms_checkbox.focus();
          e.preventDefault();
        }
      });
    }
  }
  notesToggle() {
    let notes_toggle = this.querySelector('#order-note-toggle');
    let notes_content = this.querySelector('#mini-cart-note');

    if (!notes_toggle || !notes_content) {
      return;
    }

    notes_toggle.addEventListener('click', (event) => {
      notes_content.classList.add('active');
    });
    notes_content.querySelectorAll('.button, .order-note-toggle__content-overlay').forEach((el) => {
      el.addEventListener('click', (event) => {
        notes_content.classList.remove('active');
        this.saveNotes();
      });
    });
  }
  discountToggle() {
    let discount_toggle = this.querySelector('#discount-toggle');
    let discount_content = this.querySelector('#mini-cart-discount');

    if (!discount_toggle || !discount_content) {
      return;
    }

    discount_toggle.addEventListener('click', (event) => {
      discount_content.classList.add('active');
    });
    discount_content.querySelectorAll('.discount-toggle__content-overlay').forEach((el) => {
      el.addEventListener('click', (event) => {
        discount_content.classList.remove('active');
      });
    });
  }
  saveNotes() {
    const request = fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      body: JSON.stringify({
        'note': document.getElementById('mini-cart__notes').value
      })
    });
    const SE = window.Shopify && window.Shopify.StandardEvents;
    if (SE) {
      publishStandardEvent('CartNoteUpdateEvent', {
        context: 'dialog',
        note: document.getElementById('mini-cart__notes').value,
        promise: request.then((r) => r.json()).then((cart) => ({ cart: cart && cart.token ? SE.CartNoteUpdateEvent.createCartFromAjaxResponse(cart) : null })).catch(() => ({ cart: null }))
      }, this);
    }
  }
  updateFreeShipping() {
    const free_shipping = this.querySelector('.free-shipping');

    if (free_shipping) {
      let amount_text = free_shipping.querySelector('.free-shipping--text span');
      let total = parseInt(free_shipping.dataset.cartTotal, 10);
      let minimum = Math.round(parseInt(free_shipping.dataset.minimum, 10) * (Shopify.currency.rate || 1));
      let percentage = 1;

      if (total < minimum) {
        percentage = total / minimum;

        if (amount_text) {
          let remaining = minimum - total,
            format = window.theme.settings.money_with_currency_format || "${{amount}}";
          amount_text.innerHTML = formatMoney(remaining, format);
        }
      }

      free_shipping.style.setProperty('--percentage', percentage);
    }
  }
  updateQuantity(key, quantity, line) {
    this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
    const body = JSON.stringify({
      id: key,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    dispatchCustomEvent('line-item:change:start', {
      quantity: quantity
    });

    fetch(`${theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      ...{
        body
      }
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          if (parsedState.sections) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          }
        }));

        this.notesToggle();
        this.discountToggle();
        this.updateFreeShipping();
        dispatchCustomEvent('line-item:change:end', {
          key: key,
          context: 'dialog',
          quantity: quantity,
          cart: parsedState
        });

        if (this.querySelector(`#CartDrawerItem-${line}`)) {
          this.querySelector(`#CartDrawerItem-${line}`).classList.remove('thb-loading');
        }
      });
  }
  refresh() {
    let sections = 'cart-drawer,cart-bubble';
    fetch(`${window.location.pathname}?sections=${sections}`)
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
        }));

        this.notesToggle();
        this.discountToggle();
        this.updateFreeShipping();
      });
  }
}
customElements.define('cart-drawer', CartDrawer);

/**
 *  @class
 *  @function ModalDialog
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector('[id^="ModalClose-"]');

    if (this.closeButton) {
      this.closeButton.addEventListener(
        'click',
        this.hide.bind(this)
      );
      this.closeButton.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ENTER') {
          this.hide();
        }
      });
    }

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.hide();
      }
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    if (this.dataset.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    setTimeout(() => {
      this.querySelector('[role="dialog"]').focus();
    }, 100);
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.querySelectorAll('.js-youtube').forEach((video) => {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    this.querySelectorAll('.js-vimeo').forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    this.querySelectorAll('video').forEach((video) => video.pause());
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }
      this.resizeSelect();
    }
    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText = 'visibility:hidden;position:fixed;pointer-events:none';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width = `${tempSelect.clientWidth + 10}px`;
        }
        tempSelect.remove();
      });
    }
  }
  customElements.define('resize-select', ResizeSelect);
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const _cache = new Map();

  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.drawerContent = this.drawer.querySelector('#Product-Drawer-Content');

      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      OverlayManager.registerBeforeClose(this.drawer, () => {
        setTimeout(() => {
          if (!this.drawer.classList.contains('active')) {
            this.drawerContent.innerHTML = '';
          }
        }, 260);
      });
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _onClick(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle || this.classList.contains('loading')) {
        return;
      }

      let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
      // remove double `/` in case shop might have /en or language in URL
      href = href.replace('//', '/');

      if (_cache.has(productHandle)) {
        this.renderQuickview(_cache.get(productHandle), href, productHandle);
        return;
      }

      this.classList.add('loading');
      fetch(href)
        .then((response) => response.text())
        .then((text) => {
          this.classList.remove('loading');
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content').innerHTML;

          _cache.set(productHandle, sectionInnerHTML);
          this.renderQuickview(sectionInnerHTML, href, productHandle);
        })
        .catch(() => {
          this.classList.remove('loading');
        });
    }
    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script');
      let head = document.head;
      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (oldScript.src) {
          if (document.querySelector(`script[src="${oldScript.src}"]`)) {
            return;
          }
        } else {
          newScript.textContent = oldScript.textContent;
        }
        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        } else {
          head.appendChild(newScript);
        }
      });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) {
        return;
      }

      this.drawerContent.innerHTML = sectionInnerHTML;

      this._reloadScripts(this.drawerContent);

      OverlayManager.open(this.drawer);

      requestAnimationFrame(() => {
        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
        window.dispatchEvent(new Event('resize'));
      });

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });
      addIdToRecentlyViewed(productHandle);
    }
  }
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    instantClose() {
      this.tl.timeScale(10).reverse();
    }
    animateClose() {
      this.tl.timeScale(3).reverse();
    }
    animateOpen() {
      this.tl.timeScale(1).play();
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds && typeof (recentIds) !== undefined) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

/**
 *  Animations
 */
ScrollTrigger.batch('.animations-true .products .gallery--item figure, .animations-true .product-card .product-featured-image-link', {
  start: "top 90%",
  onEnter: (elements, triggers) => {
    gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
  }
});
ScrollTrigger.batch('.animations-true .multicolumn--column-inner', {
  start: "top 90%",
  onEnter: (elements, triggers) => {
    gsap.to(elements, { y: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
  }
});
ScrollTrigger.batch('.animations-true .blog-post .featured-image--link', {
  start: "top 90%",
  onEnter: (elements, triggers) => {
    gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
  }
});

